import { apiBaseUrl, auth, fetchAppCheckToken } from '../firebase';
import { resolveMediaUrl } from './mediaApi';
import { waitForAuthUser } from './secureFunctions';
import { handleBlockedAccount } from './accountStatus';

function requireApiBase(): string {
  const base = (apiBaseUrl || '').replace(/\/$/, '');
  if (!base) {
    throw new Error('api-base-not-configured');
  }
  return base;
}

export async function getAuthHeaders(): Promise<Headers> {
  const user = auth.currentUser || await waitForAuthUser();
  if (!user) {
    throw new Error('auth-required');
  }

  const [idToken, appCheckToken] = await Promise.all([
    user.getIdToken(),
    fetchAppCheckToken().catch(() => null),
  ]);

  const headers = new Headers({
    Authorization: `Bearer ${idToken}`,
  });

  if (appCheckToken) {
    headers.set('X-Firebase-AppCheck', appCheckToken);
  }

  return headers;
}

export function buildMusicProxyUrl(url: string): string {
  if (url.startsWith('media/')) return `${requireApiBase()}/musicProxy?url=${encodeURIComponent(url)}`;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return url;
    const base = requireApiBase();
    return `${base}/musicProxy?url=${encodeURIComponent(parsed.toString())}`;
  } catch {}
  return url;
}

export async function resolveMusicPlayableUrl(urlOrKey: string): Promise<string> {
  // New path: R2 object keys are resolved to short-lived signed URLs.
  if (typeof urlOrKey === 'string' && urlOrKey.startsWith('media/')) {
    return resolveMediaUrl(urlOrKey);
  }
  // Fallback: for remote URLs, prefer the proxy since <audio> can't attach Authorization headers.
  return buildMusicProxyUrl(urlOrKey);
}

export async function fetchWithAuth(url: string, init?: RequestInit): Promise<Response> {
  const headers = await getAuthHeaders();

  // Merge caller headers
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => headers.set(key, value));
  }

  const res = await fetch(url, {
    ...init,
    headers,
  });
  if (res.status === 401) {
    res.clone().json().then((d) => handleBlockedAccount(d?.code || d?.error)).catch(() => undefined);
  }
  return res;
}

export async function fetchMusicProxyBlobUrl(storageUrl: string, init?: RequestInit): Promise<string> {
  const proxied = buildMusicProxyUrl(storageUrl);
  const res = await fetchWithAuth(proxied, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`music-proxy-failed:${res.status}:${text || res.statusText}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
